import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  VestingGraphCliff,
  VestingGraphPointStatus,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { StringKey } from '../../../../../lang/index';
import { toRound } from '../../../../../utils/get-rounded-number.util';
import {
  isAccelerationInactivePoint,
  UnvestedSummary,
  VestingGraphSeriesPoint,
} from '../model/vesting-graph.utils';

type VestingGraphTooltipProps = {
  accelerationDateLabel?: string;
  accelerationTimestamp?: null | number;
  active?: boolean;
  cliff?: VestingGraphCliff;
  cliffEndDateLabel?: string;
  /** Milestone schedules talk about the target that vested, not a frequency period. */
  isMilestoneSchedule?: boolean;
  payload?: Array<{ payload: VestingGraphSeriesPoint }>;
  totalShares: number;
  unvestedSummary: UnvestedSummary;
  variant?: 'auto' | 'cliff';
};

const TOOLTIP_TRANSITION_MS = 240;

type AnimatedFixedTooltipProps = {
  children: ReactNode;
  left: number;
  open: boolean;
  /** Ease between discrete columns. Pointer-tracked target hover stays live. */
  smoothPosition?: boolean;
  top: number;
  /** CSS transform for the outer position shell (scale lives on the inner fade layer). */
  transform?: string;
};

/**
 * Stable body portal so enter/leave opacity+scale can finish. Recharts remounts its
 * `content` node on every hover move — animating inside that tree never sticks.
 */
export const AnimatedFixedTooltip: FC<AnimatedFixedTooltipProps> = ({
  children,
  left,
  open,
  smoothPosition = true,
  top,
  transform = 'translate(-50%, calc(-100% - 8px))',
}) => {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const cachedChildrenRef = useRef(children);
  const cachedPositionRef = useRef({ left, top });

  if (open) {
    cachedChildrenRef.current = children;
    cachedPositionRef.current = { left, top };
  }

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }

    setEntered(false);
    const timeoutId = window.setTimeout(() => setMounted(false), TOOLTIP_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!mounted || !open) return;

    // Double rAF: first paint at opacity 0, then transition to visible.
    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setEntered(true));
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [mounted, open]);

  if (!mounted || typeof document === 'undefined') return null;

  // Live left/top while open so the shell can ease between bars; cache only for leave.
  const shellLeft = open ? left : cachedPositionRef.current.left;
  const shellTop = open ? top : cachedPositionRef.current.top;

  return createPortal(
    <div
      className={twMerge(
        'pointer-events-none fixed z-[100]',
        entered && smoothPosition && 'transition-[left,top] duration-200 ease-out',
      )}
      style={{ left: shellLeft, top: shellTop, transform }}
    >
      <div
        className={twMerge(
          'origin-bottom transition-[opacity,transform] duration-200 ease-out will-change-[opacity,transform]',
          entered ? 'scale-100 opacity-100' : 'scale-[0.97] opacity-0',
        )}
      >
        {cachedChildrenRef.current}
      </div>
    </div>,
    document.body,
  );
};

const TooltipCard: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div
    className={twMerge(
      'min-w-[240px] max-w-[320px] rounded-lg bg-white py-2 shadow-2xl',
      className,
    )}
  >
    {children}
  </div>
);

/**
 * The date heads the tooltip in plain dark text and is ruled off from the figures below,
 * which are the ones worth picking out in blue.
 */
const TooltipRow: FC<{
  hasDivider?: boolean;
  isHeading?: boolean;
  label: StringKey;
  value: ReactNode;
}> = ({ hasDivider = false, isHeading = false, label, value }) => (
  <div
    className={twMerge(
      'flex items-center justify-between gap-6 px-4 py-1.5',
      (hasDivider || isHeading) && 'border-b border-gray-200 pb-2',
    )}
  >
    <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-700">
      <AppFormattedMessage id={label} />
    </span>
    <span
      className={twMerge(
        'whitespace-nowrap text-[10px] font-semibold',
        isHeading ? 'text-gray-700' : 'text-brand-700',
      )}
    >
      {value}
    </span>
  </div>
);

const TooltipNote: FC<{ id: StringKey; values?: Record<string, ReactNode> }> = ({ id, values }) => (
  <p className="px-4 py-2 text-[10px] font-medium uppercase leading-4 tracking-[0.5px] text-gray-500">
    <AppFormattedMessage id={id} values={values} />
  </p>
);

const formatShares = (shares: number) => Number(toRound(shares)).toLocaleString('en-US');

const VestingGraphCliffTooltip: FC<{
  cliff: VestingGraphCliff;
  cliffEndDateLabel: string;
}> = ({ cliff, cliffEndDateLabel }) => (
  <TooltipCard>
    <TooltipRow
      hasDivider
      label="CLIFF_PERIOD"
      value={
        <span className="uppercase">
          <AppFormattedMessage id="VESTING_GRAPH_CLIFF_MONTHS" values={{ count: cliff.months }} />
        </span>
      }
    />
    <TooltipRow label="CLIFF_END_DATE" value={cliffEndDateLabel} />
    <TooltipNote id="VESTING_GRAPH_CLIFF_TOOLTIP_BODY" />
  </TooltipCard>
);

export const VestingGraphTooltip: FC<VestingGraphTooltipProps> = ({
  accelerationDateLabel,
  accelerationTimestamp,
  active,
  cliff,
  cliffEndDateLabel,
  isMilestoneSchedule = false,
  payload,
  totalShares,
  unvestedSummary,
  variant = 'auto',
}) => {
  if (variant === 'cliff') {
    if (!cliff || !cliffEndDateLabel) return null;

    return <VestingGraphCliffTooltip cliff={cliff} cliffEndDateLabel={cliffEndDateLabel} />;
  }

  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  if (point.isExpiryColumn) {
    return (
      <TooltipCard className="min-w-[280px]">
        <TooltipRow isHeading label="EXPIRY_DATE" value={point.dateLabel} />
        <TooltipNote id="VESTING_GRAPH_EXPIRY_TOOLTIP_BODY" />
      </TooltipCard>
    );
  }

  if (point.isAccelerationColumn) {
    const acceleratedShares = Math.max(0, totalShares - point.cumulativeShares);

    return (
      <TooltipCard className="min-w-[260px]">
        <TooltipRow isHeading label="ACCELERATION_DATE" value={point.dateLabel} />
        <TooltipRow label="GRANTS_VESTED_IN_THE_PERIOD" value={formatShares(acceleratedShares)} />
        <TooltipRow label="TOTAL_GRANTS_VESTED" value={formatShares(totalShares)} />
        <TooltipRow label="PERCENT_OF_TOTAL_VESTED_GRANTS" value="100%" />
      </TooltipCard>
    );
  }

  if (cliff && point.isCliffSpan && cliffEndDateLabel) {
    return <VestingGraphCliffTooltip cliff={cliff} cliffEndDateLabel={cliffEndDateLabel} />;
  }

  // Right of the acceleration marker: original schedule, not unvested equity.
  if (isAccelerationInactivePoint({ accelerationTimestamp, point })) {
    return (
      <TooltipCard className="min-w-[280px]">
        <div className="border-b border-gray-200 px-4 pb-2 pt-0.5 text-center">
          <span className="text-[10px] font-semibold uppercase leading-4 tracking-[0.5px] text-brand-700">
            <AppFormattedMessage id="VESTING_GRAPH_PROJECTED_WITHOUT_ACCELERATION" />
          </span>
        </div>
        <TooltipRow
          label="VESTING_GRAPH_WOULD_VEST_IN_THIS_PERIOD"
          value={formatShares(point.vestingStepShares)}
        />
        <TooltipNote
          id="VESTING_GRAPH_PROJECTED_WITHOUT_ACCELERATION_BODY"
          values={{ date: accelerationDateLabel ?? point.dateLabel }}
        />
      </TooltipCard>
    );
  }

  // Left of acceleration / after expiry: upcoming columns share one unvested-grants tooltip.
  if (
    point.status === VestingGraphPointStatus.UNVESTED ||
    point.status === VestingGraphPointStatus.EXPIRED
  ) {
    return (
      <TooltipCard>
        <TooltipRow label="UNVESTED_GRANTS" value={formatShares(unvestedSummary.shares)} />
        <TooltipRow
          label="PERCENT_OF_UNVESTED_GRANTS"
          value={`${toRound(unvestedSummary.percent, 0)}%`}
        />
      </TooltipCard>
    );
  }

  return (
    <TooltipCard className="min-w-[260px]">
      {point.milestoneName && (
        <div className="border-b border-gray-200 px-4 pb-2">
          <span className="text-[10px] font-semibold text-gray-700">{point.milestoneName}</span>
        </div>
      )}
      <TooltipRow
        isHeading
        label={point.isGrantStart ? 'GRANT_DATE' : 'VESTING_DATE'}
        value={point.dateLabel}
      />
      <TooltipRow
        label={isMilestoneSchedule ? 'GRANTS_VESTED_AT_MILESTONE' : 'GRANTS_VESTED_IN_THE_PERIOD'}
        value={formatShares(point.vestingStepShares)}
      />
      <TooltipRow label="TOTAL_GRANTS_VESTED" value={formatShares(point.cumulativeShares)} />
      <TooltipRow
        label="PERCENT_OF_TOTAL_VESTED_GRANTS"
        value={`${toRound(point.cumulativePercent, 0)}%`}
      />
      {!isMilestoneSchedule && (
        <TooltipRow
          label="MONTHS_TO_COMPLETE_FULL_VESTING"
          value={String(point.monthsToComplete)}
        />
      )}
    </TooltipCard>
  );
};
